import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useTranslation } from "react-i18next";
import { getAllCategorie } from "../APIs/CategoriesAPI";
import {
  createSpaCommissionRule,
  dateTimeWithLocalOffset,
  getSpaCommissionLedger,
  getSpaCommissionRules,
  getSpaInventoryProducts,
  getSpaOperators,
  getSpaTreatments,
  spaDisplayName,
} from "../APIs/SpaAPI";
import { useSnackbar } from "../contexts/SnackBarContext";
import SpaOperatorCompensationReport from "./SpaOperatorCompensationReport";

const currentPeriod = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const WEEK_DAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

const emptyRule = () => ({
  code: "",
  name: "",
  sourceType: "TREATMENT",
  calculationType: "PERCENTAGE",
  operatorId: "",
  scope: "PRODUCT",
  productId: "",
  categoryId: "",
  fixedAmount: "",
  percentage: "",
  tiers: [{ fromAmount: 0, percentage: "" }],
  effectiveFrom: "",
  effectiveTo: "",
  daysOfWeek: [],
  timeFrom: "",
  timeTo: "",
  minBaseAmount: "",
  maxBaseAmount: "",
  minQuantity: "",
  customerType: "",
  priority: 0,
  stackingBehavior: "FIRST_MATCH",
});

const money = (value) => `EUR ${Number(value || 0).toFixed(2)}`;

const localDateTimePayload = (value) => {
  if (!value) return undefined;
  const [date, time] = value.split("T");
  return date && time ? dateTimeWithLocalOffset(date, time) : undefined;
};

const dateLabel = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const normalizeCategories = (items = []) => items.map((category) => ({
  id: Number(category.id),
  name: category.name || `#${category.id}`,
})).filter((category) => Number.isInteger(category.id) && category.id > 0);

const csvCell = (value) => {
  let text = String(value ?? "");
  if (/^[\s]*[=+\-@]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
};

function SpaCommissionManager({ token, tenant, canManage }) {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const ct = useCallback((key, defaultValue, options = {}) => (
    t(`spa.finance.commissions.${key}`, { defaultValue, ...options })
  ), [t]);
  const [form, setForm] = useState(emptyRule);
  const [rules, setRules] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [operators, setOperators] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [retailProducts, setRetailProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({ operatorId: "", sourceType: "", period: currentPeriod() });
  const [setupLoading, setSetupLoading] = useState(true);
  const [ledgerLoading, setLedgerLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [ledgerError, setLedgerError] = useState("");

  const operatorOptions = useMemo(() => operators.map((operator) => ({
    id: Number(operator.id),
    label: spaDisplayName(operator, "Operator"),
  })), [operators]);
  const productOptions = form.sourceType === "TREATMENT" ? treatments : retailProducts;

  const loadSetup = useCallback(async () => {
    if (!token || !tenant) return;
    setSetupLoading(true);
    setError("");
    try {
      const [ruleData, operatorData, treatmentData, retailData, categoryData] = await Promise.all([
        getSpaCommissionRules({}, token, tenant),
        getSpaOperators(token, tenant),
        getSpaTreatments(token, tenant),
        getSpaInventoryProducts(token, tenant),
        getAllCategorie(token, tenant),
      ]);
      setRules(ruleData);
      setOperators(Array.isArray(operatorData) ? operatorData : []);
      setTreatments(Array.isArray(treatmentData) ? treatmentData : []);
      setRetailProducts(Array.isArray(retailData) ? retailData : []);
      setCategories(normalizeCategories(Array.isArray(categoryData) ? categoryData : []));
    } catch (loadError) {
      setError(loadError?.message || ct("messages.loadError", "Unable to load commission rules."));
    } finally {
      setSetupLoading(false);
    }
  }, [ct, tenant, token]);

  const loadLedger = useCallback(async (options = {}) => {
    if (!token || !tenant) return;
    setLedgerLoading(true);
    setLedgerError("");
    try {
      const data = await getSpaCommissionLedger({
        ...(filters.operatorId ? { operatorId: Number(filters.operatorId) } : {}),
        ...(filters.sourceType ? { sourceType: filters.sourceType } : {}),
        ...(filters.period ? { period: filters.period } : {}),
        limit: 500,
      }, token, tenant, { signal: options.signal });
      if (!options.signal?.aborted) setLedger(data);
    } catch (loadError) {
      if (loadError?.name !== "AbortError" && !options.signal?.aborted) {
        setLedger([]);
        setLedgerError(loadError?.message || ct("messages.ledgerError", "Unable to load commission ledger."));
      }
    } finally {
      if (!options.signal?.aborted) setLedgerLoading(false);
    }
  }, [ct, filters.operatorId, filters.period, filters.sourceType, tenant, token]);

  useEffect(() => {
    loadSetup();
  }, [loadSetup]);

  useEffect(() => {
    const controller = new AbortController();
    loadLedger({ signal: controller.signal });
    return () => controller.abort();
  }, [loadLedger]);

  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const changeSource = (sourceType) => setForm((current) => ({
    ...current,
    sourceType,
    productId: "",
    categoryId: "",
  }));

  const changeScope = (scope) => setForm((current) => ({
    ...current,
    scope,
    productId: "",
    categoryId: "",
  }));

  const updateTier = (index, field, value) => setForm((current) => ({
    ...current,
    tiers: current.tiers.map((tier, tierIndex) => (
      tierIndex === index ? { ...tier, [field]: value } : tier
    )),
  }));

  const addTier = () => setForm((current) => ({
    ...current,
    tiers: [...current.tiers, { fromAmount: "", percentage: "" }],
  }));

  const removeTier = (index) => setForm((current) => ({
    ...current,
    tiers: current.tiers.filter((_, tierIndex) => tierIndex !== index),
  }));

  const formError = useMemo(() => {
    if (!form.code.trim() || !form.name.trim()) return ct("validation.required", "Code and name are required.");
    if (!/^[A-Z0-9_]+$/.test(form.code)) return ct("validation.code", "Code may contain only letters, numbers and underscores.");
    if (form.scope === "PRODUCT" && !form.productId) return ct("validation.product", "Select a treatment or product.");
    if (form.scope === "CATEGORY" && !form.categoryId) return ct("validation.category", "Select a category.");
    if (form.calculationType === "FIXED" && Number(form.fixedAmount) <= 0) return ct("validation.fixed", "Enter a fixed amount greater than zero.");
    if (form.calculationType === "PERCENTAGE" && (Number(form.percentage) <= 0 || Number(form.percentage) > 100)) {
      return ct("validation.percentage", "Enter a percentage between 0 and 100.");
    }
    if (form.calculationType === "PROGRESSIVE") {
      const tiers = form.tiers.map((tier) => ({ fromAmount: Number(tier.fromAmount), percentage: Number(tier.percentage) }));
      if (!tiers.length || tiers[0].fromAmount !== 0 || tiers.some((tier) => tier.fromAmount < 0 || tier.percentage <= 0 || tier.percentage > 100)) {
        return ct("validation.tiers", "Progressive tiers must start at zero and use valid percentages.");
      }
      if (tiers.some((tier, index) => index > 0 && tier.fromAmount <= tiers[index - 1].fromAmount)) {
        return ct("validation.tierOrder", "Progressive thresholds must be in ascending order.");
      }
    }
    if (form.effectiveFrom && form.effectiveTo && new Date(form.effectiveTo) <= new Date(form.effectiveFrom)) {
      return ct("validation.dates", "The end must be after the start.");
    }
    if (Boolean(form.timeFrom) !== Boolean(form.timeTo) || (form.timeFrom && form.timeTo && form.timeTo <= form.timeFrom)) {
      return ct("validation.timeRange", "Enter a valid start and end time.");
    }
    if (form.minBaseAmount !== "" && Number(form.minBaseAmount) < 0) return ct("validation.minBase", "Minimum base amount cannot be negative.");
    if (form.maxBaseAmount !== "" && Number(form.maxBaseAmount) < 0) return ct("validation.maxBase", "Maximum base amount cannot be negative.");
    if (form.minBaseAmount !== "" && form.maxBaseAmount !== "" && Number(form.maxBaseAmount) < Number(form.minBaseAmount)) {
      return ct("validation.baseRange", "Maximum base amount must be greater than or equal to the minimum.");
    }
    if (form.minQuantity !== "" && Number(form.minQuantity) <= 0) return ct("validation.minQuantity", "Minimum quantity must be greater than zero.");
    if (!Number.isInteger(Number(form.priority)) || Number(form.priority) < -100000 || Number(form.priority) > 100000) {
      return ct("validation.priority", "Priority must be an integer between -100000 and 100000.");
    }
    return "";
  }, [ct, form]);

  const saveRule = async () => {
    if (formError || !canManage) return;
    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      sourceType: form.sourceType,
      calculationType: form.calculationType,
      ...(form.operatorId ? { operatorId: Number(form.operatorId) } : {}),
      ...(form.scope === "PRODUCT" ? { productId: Number(form.productId) } : {}),
      ...(form.scope === "CATEGORY" ? { categoryId: Number(form.categoryId) } : {}),
      ...(form.calculationType === "FIXED" ? { fixedAmount: Number(form.fixedAmount) } : {}),
      ...(form.calculationType === "PERCENTAGE" ? { percentage: Number(form.percentage) } : {}),
      ...(form.calculationType === "PROGRESSIVE" ? {
        tiers: form.tiers.map((tier) => ({
          fromAmount: Number(tier.fromAmount),
          percentage: Number(tier.percentage),
        })),
      } : {}),
      ...(form.effectiveFrom ? { effectiveFrom: localDateTimePayload(form.effectiveFrom) } : {}),
      ...(form.effectiveTo ? { effectiveTo: localDateTimePayload(form.effectiveTo) } : {}),
      ...(form.daysOfWeek.length ? { daysOfWeek: form.daysOfWeek } : {}),
      ...(form.timeFrom ? { timeFrom: form.timeFrom, timeTo: form.timeTo } : {}),
      ...(form.minBaseAmount !== "" ? { minBaseAmount: Number(form.minBaseAmount) } : {}),
      ...(form.maxBaseAmount !== "" ? { maxBaseAmount: Number(form.maxBaseAmount) } : {}),
      ...(form.minQuantity !== "" ? { minQuantity: Number(form.minQuantity) } : {}),
      ...(form.customerType ? { customerType: form.customerType } : {}),
      priority: Number(form.priority),
      stackingBehavior: form.stackingBehavior,
    };
    setSaving(true);
    try {
      await createSpaCommissionRule(payload, token, tenant);
      setForm(emptyRule());
      await loadSetup();
      showSnackbar(ct("messages.saved", "Commission rule saved."), "success");
    } catch (saveError) {
      showSnackbar(saveError?.message || ct("messages.saveError", "Unable to save commission rule."), "error");
    } finally {
      setSaving(false);
    }
  };

  const startNewVersion = (rule) => {
    setForm({
      ...emptyRule(),
      code: rule.code || "",
      name: rule.name || "",
      sourceType: rule.sourceType || "TREATMENT",
      calculationType: rule.calculationType || "PERCENTAGE",
      operatorId: rule.operator?.id ? String(rule.operator.id) : "",
      scope: rule.product ? "PRODUCT" : rule.category ? "CATEGORY" : "GLOBAL",
      productId: rule.product?.id ? String(rule.product.id) : "",
      categoryId: rule.category?.id ? String(rule.category.id) : "",
      fixedAmount: rule.fixedAmount ?? "",
      percentage: rule.percentage ?? "",
      tiers: Array.isArray(rule.tiers) && rule.tiers.length
        ? rule.tiers.map((tier) => ({ fromAmount: tier.fromAmount, percentage: tier.percentage }))
        : [{ fromAmount: 0, percentage: "" }],
      daysOfWeek: Array.isArray(rule.daysOfWeek) ? rule.daysOfWeek : [],
      timeFrom: rule.timeFrom || "",
      timeTo: rule.timeTo || "",
      minBaseAmount: rule.minBaseAmount ?? "",
      maxBaseAmount: rule.maxBaseAmount ?? "",
      minQuantity: rule.minQuantity ?? "",
      customerType: rule.customerType || "",
      priority: rule.priority ?? 0,
      stackingBehavior: rule.stackingBehavior || "FIRST_MATCH",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const ruleTarget = (rule) => {
    if (rule.product) return rule.product.name;
    if (rule.category) return rule.category.name;
    return ct("scope.GLOBAL", "All");
  };

  const ruleValue = (rule) => {
    if (rule.calculationType === "FIXED") return money(rule.fixedAmount);
    if (rule.calculationType === "PERCENTAGE") return `${Number(rule.percentage || 0)}%`;
    return (rule.tiers || []).map((tier) => `${money(tier.fromAmount)}: ${tier.percentage}%`).join(" | ");
  };

  const ruleConditions = (rule) => {
    const values = [];
    if (rule.daysOfWeek?.length) values.push(rule.daysOfWeek.map((day) => ct(`days.${day}`, day)).join(", "));
    if (rule.timeFrom && rule.timeTo) values.push(`${rule.timeFrom}-${rule.timeTo}`);
    if (rule.minBaseAmount != null || rule.maxBaseAmount != null) values.push(`${money(rule.minBaseAmount || 0)}-${rule.maxBaseAmount == null ? ct("noMaximum", "No maximum") : money(rule.maxBaseAmount)}`);
    if (rule.minQuantity != null) values.push(ct("conditionQuantity", "Qty >= {{value}}", { value: rule.minQuantity }));
    if (rule.customerType) values.push(ct(`customerType.${rule.customerType}`, rule.customerType));
    values.push(`${ct("fields.priority", "Priority")}: ${rule.priority ?? 0}`);
    values.push(ct(`stacking.${rule.stackingBehavior || "FIRST_MATCH"}`, rule.stackingBehavior || "FIRST_MATCH"));
    return values;
  };

  const exportLedger = () => {
    const rows = [
      ["date", "operator", "source", "entry_type", "rule", "base_amount", "commission", "reference", "reason"],
      ...ledger.map((entry) => [
        entry.occurredAt,
        spaDisplayName(entry.operator, "Operator"),
        entry.sourceType,
        entry.entryType,
        entry.rule?.name || entry.rule?.code || "",
        entry.baseAmount,
        entry.amount,
        entry.appointmentId ? `appointment:${entry.appointmentId}` : entry.orderProductId ? `order-product:${entry.orderProductId}` : "",
        entry.reason || "",
      ]),
    ];
    const blob = new Blob([`\uFEFF${rows.map((row) => row.map(csvCell).join(",")).join("\r\n")}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `spa-operator-commissions-${filters.period || "all"}.csv`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1}>
            <Box>
              <Typography variant="h6" fontWeight={850}>{ct("ruleTitle", "Commission rules")}</Typography>
              <Typography variant="body2" color="text.secondary">
                {ct("ruleHelp", "Create effective versions for treatment and retail compensation.")}
              </Typography>
            </Box>
            <Button startIcon={<RefreshIcon />} variant="outlined" onClick={() => Promise.all([loadSetup(), loadLedger()])}
              disabled={setupLoading || ledgerLoading || saving}>
              {t("common.actions.refresh", { defaultValue: "Refresh" })}
            </Button>
          </Stack>

          {!canManage ? <Alert severity="info">{ct("readOnly", "You can view commissions but cannot create rules.")}</Alert> : null}
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Grid container spacing={1.5}>
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" label={ct("fields.code", "Code")} value={form.code}
                disabled={!canManage || saving} inputProps={{ maxLength: 50 }}
                onChange={(event) => setField("code", event.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ""))} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" label={ct("fields.name", "Rule name")} value={form.name}
                disabled={!canManage || saving} onChange={(event) => setField("name", event.target.value)} />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="commission-source-label">{ct("fields.source", "Source")}</InputLabel>
                <Select labelId="commission-source-label" label={ct("fields.source", "Source")} value={form.sourceType} disabled={!canManage || saving}
                  onChange={(event) => changeSource(event.target.value)}>
                  {['TREATMENT', 'RETAIL'].map((value) => <MenuItem key={value} value={value}>{ct(`source.${value}`, value)}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="commission-operator-label">{ct("fields.operator", "Operator")}</InputLabel>
                <Select labelId="commission-operator-label" label={ct("fields.operator", "Operator")} value={form.operatorId} disabled={!canManage || saving}
                  onChange={(event) => setField("operatorId", event.target.value)}>
                  <MenuItem value="">{ct("allOperators", "All operators")}</MenuItem>
                  {operatorOptions.map((operator) => <MenuItem key={operator.id} value={String(operator.id)}>{operator.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="commission-scope-label">{ct("fields.scope", "Applies to")}</InputLabel>
                <Select labelId="commission-scope-label" label={ct("fields.scope", "Applies to")} value={form.scope} disabled={!canManage || saving}
                  onChange={(event) => changeScope(event.target.value)}>
                  {['GLOBAL', 'PRODUCT', 'CATEGORY'].map((value) => <MenuItem key={value} value={value}>{ct(`scope.${value}`, value)}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            {form.scope === "PRODUCT" ? <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="commission-product-label">{form.sourceType === "TREATMENT" ? ct("fields.treatment", "Treatment") : ct("fields.product", "Retail product")}</InputLabel>
                <Select labelId="commission-product-label" label={form.sourceType === "TREATMENT" ? ct("fields.treatment", "Treatment") : ct("fields.product", "Retail product")}
                  value={form.productId} disabled={!canManage || saving} onChange={(event) => setField("productId", event.target.value)}>
                  {productOptions.map((product) => <MenuItem key={product.id} value={String(product.id)}>{product.name || `#${product.id}`}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid> : null}
            {form.scope === "CATEGORY" ? <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="commission-category-label">{ct("fields.category", "Category")}</InputLabel>
                <Select labelId="commission-category-label" label={ct("fields.category", "Category")} value={form.categoryId} disabled={!canManage || saving}
                  onChange={(event) => setField("categoryId", event.target.value)}>
                  {categories.map((category) => <MenuItem key={category.id} value={String(category.id)}>{category.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid> : null}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="commission-calculation-label">{ct("fields.calculation", "Calculation")}</InputLabel>
                <Select labelId="commission-calculation-label" label={ct("fields.calculation", "Calculation")} value={form.calculationType} disabled={!canManage || saving}
                  onChange={(event) => setField("calculationType", event.target.value)}>
                  {['PERCENTAGE', 'FIXED', 'PROGRESSIVE'].map((value) => <MenuItem key={value} value={value}>{ct(`calculation.${value}`, value)}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            {form.calculationType === "PERCENTAGE" ? <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" type="number" label={ct("fields.percentage", "Percentage")}
                value={form.percentage} disabled={!canManage || saving} inputProps={{ min: 0.01, max: 100, step: 0.01 }}
                onChange={(event) => setField("percentage", event.target.value)} />
            </Grid> : null}
            {form.calculationType === "FIXED" ? <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" type="number" label={ct("fields.fixedAmount", "Fixed amount")}
                value={form.fixedAmount} disabled={!canManage || saving} inputProps={{ min: 0.01, step: 0.01 }}
                onChange={(event) => setField("fixedAmount", event.target.value)} />
            </Grid> : null}
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" type="datetime-local" InputLabelProps={{ shrink: true }}
                label={ct("fields.effectiveFrom", "Effective from")} value={form.effectiveFrom} disabled={!canManage || saving}
                onChange={(event) => setField("effectiveFrom", event.target.value)} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" type="datetime-local" InputLabelProps={{ shrink: true }}
                label={ct("fields.effectiveTo", "Effective to")} value={form.effectiveTo} disabled={!canManage || saving}
                onChange={(event) => setField("effectiveTo", event.target.value)} />
            </Grid>
          </Grid>

          {form.calculationType === "PROGRESSIVE" ? (
            <Stack spacing={1}>
              <Typography fontWeight={800}>{ct("tiers", "Progressive thresholds")}</Typography>
              {form.tiers.map((tier, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="center">
                  <TextField size="small" type="number" label={ct("fields.fromAmount", "From amount")}
                    value={tier.fromAmount} disabled={!canManage || saving || index === 0} inputProps={{ min: 0, step: 0.01 }}
                    onChange={(event) => updateTier(index, "fromAmount", event.target.value)} />
                  <TextField size="small" type="number" label={ct("fields.percentage", "Percentage")}
                    value={tier.percentage} disabled={!canManage || saving} inputProps={{ min: 0.01, max: 100, step: 0.01 }}
                    onChange={(event) => updateTier(index, "percentage", event.target.value)} />
                  <Tooltip title={ct("actions.removeTier", "Remove threshold")}>
                    <span><IconButton color="error" disabled={!canManage || saving || form.tiers.length === 1}
                      onClick={() => removeTier(index)}><DeleteOutlineIcon /></IconButton></span>
                  </Tooltip>
                </Stack>
              ))}
              <Button sx={{ alignSelf: "flex-start" }} startIcon={<AddIcon />} onClick={addTier} disabled={!canManage || saving || form.tiers.length >= 20}>
                {ct("actions.addTier", "Add threshold")}
              </Button>
            </Stack>
          ) : null}

          <Box>
            <Typography fontWeight={850} sx={{ mb: 1 }}>{ct("specialConditions", "Special conditions")}</Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="commission-days-label">{ct("fields.daysOfWeek", "Days of week")}</InputLabel>
                  <Select
                    multiple
                    labelId="commission-days-label"
                    label={ct("fields.daysOfWeek", "Days of week")}
                    value={form.daysOfWeek}
                    disabled={!canManage || saving}
                    onChange={(event) => setField("daysOfWeek", typeof event.target.value === "string" ? event.target.value.split(",") : event.target.value)}
                    renderValue={(selected) => selected.map((day) => ct(`days.${day}`, day)).join(", ")}
                  >
                    {WEEK_DAYS.map((day) => (
                      <MenuItem key={day} value={day}>
                        <Checkbox checked={form.daysOfWeek.includes(day)} />
                        <ListItemText primary={ct(`days.${day}`, day)} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth size="small" type="time" InputLabelProps={{ shrink: true }} label={ct("fields.timeFrom", "Time from")}
                  value={form.timeFrom} disabled={!canManage || saving} onChange={(event) => setField("timeFrom", event.target.value)} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth size="small" type="time" InputLabelProps={{ shrink: true }} label={ct("fields.timeTo", "Time to")}
                  value={form.timeTo} disabled={!canManage || saving} onChange={(event) => setField("timeTo", event.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth size="small" type="number" label={ct("fields.minBaseAmount", "Minimum base amount")}
                  value={form.minBaseAmount} disabled={!canManage || saving} inputProps={{ min: 0, step: 0.01 }} onChange={(event) => setField("minBaseAmount", event.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth size="small" type="number" label={ct("fields.maxBaseAmount", "Maximum base amount")}
                  value={form.maxBaseAmount} disabled={!canManage || saving} inputProps={{ min: 0, step: 0.01 }} onChange={(event) => setField("maxBaseAmount", event.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth size="small" type="number" label={ct("fields.minQuantity", "Minimum quantity")}
                  value={form.minQuantity} disabled={!canManage || saving} inputProps={{ min: 0.000001, step: 1 }} onChange={(event) => setField("minQuantity", event.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="commission-customer-type-label">{ct("fields.customerType", "Customer type")}</InputLabel>
                  <Select labelId="commission-customer-type-label" label={ct("fields.customerType", "Customer type")} value={form.customerType}
                    disabled={!canManage || saving} onChange={(event) => setField("customerType", event.target.value)}>
                    <MenuItem value="">{ct("customerType.ALL", "All customers")}</MenuItem>
                    {["INTERNAL", "EXTERNAL"].map((value) => <MenuItem key={value} value={value}>{ct(`customerType.${value}`, value)}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth size="small" type="number" label={ct("fields.priority", "Priority")}
                  value={form.priority} disabled={!canManage || saving} inputProps={{ min: -100000, max: 100000, step: 1 }} onChange={(event) => setField("priority", event.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="commission-stacking-label">{ct("fields.stackingBehavior", "Stacking")}</InputLabel>
                  <Select labelId="commission-stacking-label" label={ct("fields.stackingBehavior", "Stacking")} value={form.stackingBehavior}
                    disabled={!canManage || saving} onChange={(event) => setField("stackingBehavior", event.target.value)}>
                    {["FIRST_MATCH", "STACK"].map((value) => <MenuItem key={value} value={value}>{ct(`stacking.${value}`, value)}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {formError && (form.code || form.name) ? <Alert severity="warning">{formError}</Alert> : null}
          <Stack direction="row" spacing={1}>
            <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
              disabled={!canManage || saving || Boolean(formError)} onClick={saveRule}>
              {ct("actions.saveRule", "Save rule version")}
            </Button>
            <Button startIcon={<RestartAltIcon />} disabled={saving} onClick={() => setForm(emptyRule())}>
              {ct("actions.clear", "Clear")}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined">
        <Box sx={{ px: 2, pt: 2 }}><Typography variant="h6" fontWeight={850}>{ct("ruleList", "Rule versions")}</Typography></Box>
        <TableContainer>
          <Table size="small" aria-label={ct("ruleList", "Rule versions")}>
            <TableHead><TableRow>
              <TableCell>{ct("columns.rule", "Rule")}</TableCell><TableCell>{ct("fields.source", "Source")}</TableCell>
              <TableCell>{ct("fields.operator", "Operator")}</TableCell><TableCell>{ct("fields.scope", "Applies to")}</TableCell>
              <TableCell>{ct("fields.calculation", "Calculation")}</TableCell><TableCell>{ct("columns.period", "Effective period")}</TableCell>
              <TableCell>{ct("columns.conditions", "Special conditions")}</TableCell>
              <TableCell align="right">{t("spa.fields.actions", { defaultValue: "Actions" })}</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {setupLoading ? <TableRow><TableCell colSpan={8}><CircularProgress size={18} /></TableCell></TableRow> : null}
              {!setupLoading && !rules.length ? <TableRow><TableCell colSpan={8}>{ct("emptyRules", "No commission rules found.")}</TableCell></TableRow> : null}
              {rules.map((rule) => <TableRow key={rule.id} hover>
                <TableCell><Typography fontWeight={800}>{rule.name}</Typography><Typography variant="caption">{rule.code} v{rule.version}</Typography></TableCell>
                <TableCell>{ct(`source.${rule.sourceType}`, rule.sourceType)}</TableCell>
                <TableCell>{rule.operator ? spaDisplayName(rule.operator, "Operator") : ct("allOperators", "All operators")}</TableCell>
                <TableCell>{ruleTarget(rule)}</TableCell>
                <TableCell>{ct(`calculation.${rule.calculationType}`, rule.calculationType)}: {ruleValue(rule)}</TableCell>
                <TableCell>{dateLabel(rule.effectiveFrom)} - {dateLabel(rule.effectiveTo)}</TableCell>
                <TableCell><Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {ruleConditions(rule).map((condition) => <Chip key={condition} size="small" label={condition} />)}
                </Stack></TableCell>
                <TableCell align="right"><Button size="small" disabled={!canManage} onClick={() => startNewVersion(rule)}>{ct("actions.newVersion", "New version")}</Button></TableCell>
              </TableRow>)}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <SpaOperatorCompensationReport token={token} tenant={tenant} operators={operators} />

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1}>
            <Box><Typography variant="h6" fontWeight={850}>{ct("ledgerTitle", "Operator commission ledger")}</Typography>
              <Typography variant="body2" color="text.secondary">{ct("ledgerHelp", "Immutable monthly earnings and reversals.")}</Typography></Box>
            <Button startIcon={<DownloadIcon />} variant="outlined" disabled={!ledger.length || ledgerLoading} onClick={exportLedger}>
              {ct("actions.export", "Export CSV")}
            </Button>
          </Stack>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}><FormControl fullWidth size="small"><InputLabel id="commission-filter-operator-label">{ct("fields.operator", "Operator")}</InputLabel>
              <Select labelId="commission-filter-operator-label" label={ct("fields.operator", "Operator")} value={filters.operatorId} onChange={(event) => setFilters((current) => ({ ...current, operatorId: event.target.value }))}>
                <MenuItem value="">{ct("allOperators", "All operators")}</MenuItem>{operatorOptions.map((operator) => <MenuItem key={operator.id} value={String(operator.id)}>{operator.label}</MenuItem>)}
              </Select></FormControl></Grid>
            <Grid item xs={12} md={4}><FormControl fullWidth size="small"><InputLabel id="commission-filter-source-label">{ct("fields.source", "Source")}</InputLabel>
              <Select labelId="commission-filter-source-label" label={ct("fields.source", "Source")} value={filters.sourceType} onChange={(event) => setFilters((current) => ({ ...current, sourceType: event.target.value }))}>
                <MenuItem value="">{ct("allSources", "All sources")}</MenuItem>{['TREATMENT', 'RETAIL'].map((value) => <MenuItem key={value} value={value}>{ct(`source.${value}`, value)}</MenuItem>)}
              </Select></FormControl></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth size="small" type="month" InputLabelProps={{ shrink: true }}
              label={ct("fields.month", "Month")} value={filters.period} onChange={(event) => setFilters((current) => ({ ...current, period: event.target.value }))} /></Grid>
          </Grid>
          {ledgerError ? <Alert severity="error">{ledgerError}</Alert> : null}
          <TableContainer>
            <Table size="small" aria-label={ct("ledgerTitle", "Operator commission ledger")}>
              <TableHead><TableRow>
                <TableCell>{ct("columns.date", "Date")}</TableCell><TableCell>{ct("fields.operator", "Operator")}</TableCell>
                <TableCell>{ct("fields.source", "Source")}</TableCell><TableCell>{ct("columns.rule", "Rule")}</TableCell>
                <TableCell align="right">{ct("columns.base", "Base")}</TableCell><TableCell align="right">{ct("columns.commission", "Commission")}</TableCell>
                <TableCell>{ct("columns.reference", "Reference")}</TableCell><TableCell>{ct("columns.status", "Status")}</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {ledgerLoading ? <TableRow><TableCell colSpan={8}><CircularProgress size={18} /></TableCell></TableRow> : null}
                {!ledgerLoading && !ledger.length ? <TableRow><TableCell colSpan={8}>{ct("emptyLedger", "No commission entries for these filters.")}</TableCell></TableRow> : null}
                {ledger.map((entry) => <TableRow key={entry.id} hover>
                  <TableCell>{dateLabel(entry.occurredAt)}</TableCell><TableCell>{spaDisplayName(entry.operator, "Operator")}</TableCell>
                  <TableCell>{ct(`source.${entry.sourceType}`, entry.sourceType)}</TableCell><TableCell>{entry.rule?.name || entry.rule?.code || "-"}</TableCell>
                  <TableCell align="right">{money(entry.baseAmount)}</TableCell><TableCell align="right">{money(entry.amount)}</TableCell>
                  <TableCell>{entry.appointmentId ? `#${entry.appointmentId}` : entry.orderProductId ? `Retail #${entry.orderProductId}` : "-"}</TableCell>
                  <TableCell><Chip size="small" color={entry.entryType === "REVERSAL" ? "warning" : "success"} label={ct(`entry.${entry.entryType}`, entry.entryType)} /></TableCell>
                </TableRow>)}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Paper>
    </Stack>
  );
}

export default SpaCommissionManager;
